import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { updateInvoice } from "../../../../services/Admin/InvoicesApi";

const EditeInvoiceData = ({ open, onClose, invoice, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    service_ratio: 0,
    delivery_service_ratio: 0,
    tax_ratio: 0,
    discount_ratio: 0,
    date: "",
  });

  const [calculatedValues, setCalculatedValues] = useState({
    sub_total: 0,
    service: 0,
    delivery_service: 0,
    tax: 0,
    discount: 0,
    total_amount: 0,
  });

  // تحميل بيانات الفاتورة الحالية عند فتح الديالوج
  useEffect(() => {
    if (open && invoice) {
      setFormData({
        service_ratio: parseFloat(invoice.service_ratio) || 0,
        delivery_service_ratio: parseFloat(invoice.delivery_service_ratio) || 0,
        tax_ratio: parseFloat(invoice.tax_ratio) || 0,
        discount_ratio: parseFloat(invoice.discount_ratio) || 0,
        date: invoice.date || new Date().toISOString().split("T")[0],
      });
      setError("");
      setSuccess(false);

      // تعيين القيم الحالية
      const subTotal = parseFloat(invoice.sub_total) || 0;
      setCalculatedValues({
        sub_total: subTotal,
        service: parseFloat(invoice.service) || 0,
        delivery_service: parseFloat(invoice.delivery_service) || 0,
        tax: parseFloat(invoice.tax) || 0,
        discount: parseFloat(invoice.discount) || 0,
        total_amount: parseFloat(invoice.total_amount) || 0,
      });
    }
  }, [open, invoice]);

  // إعادة حساب القيم عند تغيير النسب
  useEffect(() => {
    if (invoice) {
      const subTotal = parseFloat(invoice.sub_total) || 0;

      const service = (subTotal * formData.service_ratio) / 100;
      const deliveryService = (subTotal * formData.delivery_service_ratio) / 100;
      const tax = (subTotal * formData.tax_ratio) / 100;
      const discount = (subTotal * formData.discount_ratio) / 100;

      const totalAmount = subTotal + service + deliveryService + tax - discount;

      setCalculatedValues({
        sub_total: subTotal,
        service: parseFloat(service.toFixed(2)),
        delivery_service: parseFloat(deliveryService.toFixed(2)),
        tax: parseFloat(tax.toFixed(2)),
        discount: parseFloat(discount.toFixed(2)),
        total_amount: parseFloat(totalAmount.toFixed(2)),
      });
    }
  }, [formData, invoice]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.date) {
      setError("التاريخ مطلوب");
      return false;
    }

    // التحقق من أن النسب أرقام صحيحة
    if (
      formData.service_ratio < 0 ||
      formData.delivery_service_ratio < 0 ||
      formData.tax_ratio < 0 ||
      formData.discount_ratio < 0
    ) {
      setError("النسب يجب أن تكون أكبر من أو تساوي صفر");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const invoiceData = {
        service: calculatedValues.service,
        delivery_service: calculatedValues.delivery_service,
        tax: calculatedValues.tax,
        discount: calculatedValues.discount,
        service_ratio: parseFloat(formData.service_ratio),
        delivery_service_ratio: parseFloat(formData.delivery_service_ratio),
        tax_ratio: parseFloat(formData.tax_ratio),
        discount_ratio: parseFloat(formData.discount_ratio),
        sub_total: calculatedValues.sub_total,
        total_amount: calculatedValues.total_amount,
        date: formData.date,
      };

      const response = await updateInvoice(invoice.id, invoiceData);

      setSuccess(true);

      // إغلاق الديالوج بعد 1.5 ثانية
      setTimeout(() => {
        onUpdated && onUpdated(response);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("خطأ في تعديل الفاتورة:", err);
      setError(err.message || "حدث خطأ في تعديل الفاتورة");
    } finally {
      setLoading(false);
    }
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <EditIcon />
        تعديل فاتورة #{invoice.id}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            تم تعديل الفاتورة بنجاح
          </Alert>
        )}

        {/* Invoice Information */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            بيانات الفاتورة:
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>رقم الفاتورة:</strong> #{invoice.id}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>رقم الطلب:</strong> #{invoice.order_id}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" sx={{ color: "primary.main" }}>
                <strong>الإجمالي الفرعي:</strong> {parseFloat(invoice.sub_total).toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2">
                <strong>الإجمالي الحالي:</strong> {parseFloat(invoice.total_amount).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            تعديل حسابات الفاتورة
          </Typography>
        </Divider>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* التاريخ */}
          <TextField
            label="تاريخ الفاتورة"
            name="date"
            type="date"
            value={formData.date}
            onChange={handleChange}
            fullWidth
            disabled={loading}
            InputLabelProps={{
              shrink: true,
            }}
            required
          />

          <Grid container spacing={2}>
            {/* نسبة الخدمة */}
            <Grid item xs={6} md={3}>
              <TextField
                label="نسبة الخدمة (%)"
                name="service_ratio"
                type="number"
                value={formData.service_ratio}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>

            {/* نسبة خدمة التوصيل */}
            <Grid item xs={6} md={3}>
              <TextField
                label="نسبة التوصيل (%)"
                name="delivery_service_ratio"
                type="number"
                value={formData.delivery_service_ratio}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>

            {/* نسبة الضريبة */}
            <Grid item xs={6} md={3}>
              <TextField
                label="نسبة الضريبة (%)"
                name="tax_ratio"
                type="number"
                value={formData.tax_ratio}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>

            {/* نسبة الخصم */}
            <Grid item xs={6} md={3}>
              <TextField
                label="نسبة الخصم (%)"
                name="discount_ratio"
                type="number"
                value={formData.discount_ratio}
                onChange={handleChange}
                fullWidth
                disabled={loading}
                inputProps={{
                  min: 0,
                  step: 0.1,
                }}
              />
            </Grid>
          </Grid>

          {/* Summary Box */}
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#fff3e0",
              border: "1px solid #ff9800",
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, fontWeight: "bold", color: "warning.main" }}
            >
              ملخص الفاتورة الجديد:
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">الإجمالي الفرعي:</Typography>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {calculatedValues.sub_total.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="success.main">
                  + رسوم الخدمة ({formData.service_ratio}%):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold" }}
                  color="success.main"
                >
                  {calculatedValues.service.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="success.main">
                  + رسوم التوصيل ({formData.delivery_service_ratio}%):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold" }}
                  color="success.main"
                >
                  {calculatedValues.delivery_service.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="success.main">
                  + الضريبة ({formData.tax_ratio}%):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold" }}
                  color="success.main"
                >
                  {calculatedValues.tax.toFixed(2)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="error.main">
                  - الخصم ({formData.discount_ratio}%):
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold" }}
                  color="error.main"
                >
                  {calculatedValues.discount.toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  الإجمالي النهائي الجديد:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "warning.main" }}
                >
                  {calculatedValues.total_amount.toFixed(2)}
                </Typography>
              </Box>

              {/* Comparison */}
              {parseFloat(invoice.total_amount) !== calculatedValues.total_amount && (
                <Box
                  sx={{
                    mt: 1,
                    p: 1,
                    backgroundColor: "info.light",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="caption" sx={{ color: "info.dark" }}>
                    الفرق:{" "}
                    {(calculatedValues.total_amount - parseFloat(invoice.total_amount)).toFixed(
                      2
                    )}{" "}
                    (القديم: {parseFloat(invoice.total_amount).toFixed(2)})
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={loading}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {loading && <CircularProgress size={20} color="inherit" />}
          {loading ? "جاري الحفظ..." : "حفظ التعديلات"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditeInvoiceData;
